import React, { useState, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Camera, Upload, X, AlertCircle, Check, User } from 'lucide-react';
import { motion } from 'framer-motion';
import { uploadAvatarToStorage } from '../../services/userService';

interface AddPhotoSheetProps {
  onClose: (result: boolean) => void;
}

const AddPhotoSheet: React.FC<AddPhotoSheetProps> = ({ onClose }) => {
  const { currentUser, updateUser } = useAuth();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate type (allow JPG/PNG/HEIC/HEIF by MIME or extension)
    const ok =
      /image\/(jpeg|png|heic|heif)/.test(file.type) ||
      /\.(jpe?g|png|heic|heif)$/i.test(file.name);

    if (!ok) {
      setError('Please choose a JPG, PNG, or HEIC image.');
      setSelectedFile(null);
      setPreviewUrl(null);
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('File size exceeds 5MB limit.');
      setSelectedFile(null);
      setPreviewUrl(null);
      return;
    }

    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setError('');
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file first.');
      return;
    }
    if (!currentUser?.id) {
      setError('You must be signed in to upload a photo.');
      return;
    }
    if (isUploading) return;

    setIsUploading(true);
    setError('');

    try {
      // Upload the avatar (function gets user from auth context)
      const imageUrl = await uploadAvatarToStorage(selectedFile);

      if (!imageUrl) {
        setError('Failed to upload image. Please try again.');
        setIsUploading(false);
        return;
      }

      // Update profile with the new avatar URL
      await updateUser({ avatarUrl: imageUrl });

      setSuccess(true);

      // Resolve success so OnboardingCoach can advance immediately
      onClose(true);
    } catch (err) {
      console.error('[avatar] upload error', err);
      setError(err instanceof Error ? err.message : 'An error occurred during upload.');
    } finally {
      setIsUploading(false);
    }
  };

  // Step 1 = Choose photo, Step 2 = Upload
  const currentStep = selectedFile ? 2 : 1;

  return (
    <div className="p-4 sm:p-6">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-lg sm:text-xl font-semibold text-contrast flex items-center">
            <Camera size={20} className="mr-2 text-secondary" />
            Add Profile Photo
          </h2>
          <p className="text-sm text-contrast-600 mt-1">
            Complete your profile to pass the Vybe Check 💫
          </p>
        </div>
        <button
          onClick={() => onClose(false)}
          className="text-contrast-500 hover:text-contrast p-1 rounded-full min-h-[44px] min-w-[44px] flex items-center justify-center"
        >
          <X size={20} />
        </button>
      </div>

      {/* Step Indicator */}
      <div className="flex items-center justify-center gap-4 mb-6">
        <div className="flex flex-col items-center">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
            currentStep === 1
              ? 'bg-secondary text-white'
              : 'bg-green-500 text-white'
          }`}>
            {currentStep > 1 ? <Check size={20} /> : '1'}
          </div>
          <span className={`text-xs mt-1 transition-colors ${
            currentStep === 1 ? 'text-secondary font-semibold' : 'text-contrast-400'
          }`}>Choose Photo</span>
        </div>

        <div className={`h-0.5 w-12 transition-colors ${
          currentStep === 2 ? 'bg-secondary' : 'bg-primary-200'
        }`}></div>

        <div className="flex flex-col items-center">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
            currentStep === 2
              ? 'bg-secondary text-white'
              : 'bg-primary-200 text-contrast-400'
          }`}>
            2
          </div>
          <span className={`text-xs mt-1 transition-colors ${
            currentStep === 2 ? 'text-secondary font-semibold' : 'text-contrast-400'
          }`}>Upload</span>
        </div>
      </div>

      {success ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center py-6"
        >
          <div className="w-16 h-16 bg-accent2/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check size={32} className="text-accent2" />
          </div>
          <h3 className="text-xl font-semibold mb-2 text-contrast">Photo Added!</h3>
          <p className="text-contrast-400">Your profile photo has been updated.</p>
        </motion.div>
      ) : (
        <>
          <p className="text-contrast-400 mb-6">
            Add a profile photo to help others recognize you and make your profile more engaging.
          </p>

          {error && (
            <div className="mb-4 bg-secondary/10 p-3 rounded-md flex items-start">
              <AlertCircle
                size={18}
                className="mr-2 mt-0.5 text-secondary flex-shrink-0"
              />
              <p className="text-secondary text-sm">{error}</p>
            </div>
          )}

          <div className="flex flex-col items-center mb-6">
            <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-primary-200 flex items-center justify-center bg-primary-100 text-contrast mb-4">
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt="Profile Preview"
                  className="w-full h-full object-cover"
                />
              ) : currentUser?.avatarUrl ? (
                <img
                  src={currentUser.avatarUrl}
                  alt="Current Profile"
                  className="w-full h-full object-cover"
                />
              ) : (
                <User size={40} />
              )}
            </div>

            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept="image/jpeg,image/png,image/heic,image/heif"
              className="hidden"
            />

            <button
              onClick={() => fileInputRef.current?.click()}
              className={`px-6 py-3 rounded-md transition-all flex items-center justify-center font-semibold min-h-[44px] ${
                currentStep === 1
                  ? 'bg-secondary text-white hover:bg-secondary-600 ring-2 ring-secondary ring-offset-2 ring-offset-theme-cards'
                  : 'bg-primary-200 text-contrast hover:bg-primary-300'
              }`}
            >
              <Upload size={16} className="mr-2" />
              {selectedFile ? 'Change Photo' : 'Choose Photo'}
            </button>
          </div>

          <div className="flex flex-col space-y-3">
            <button
              onClick={handleUpload}
              disabled={!selectedFile || isUploading}
              className={`w-full py-3 rounded-md transition-all flex items-center justify-center font-semibold min-h-[44px] ${
                selectedFile && !isUploading
                  ? 'bg-secondary text-white hover:bg-secondary-600 ring-2 ring-secondary ring-offset-2 ring-offset-theme-cards'
                  : 'bg-primary-200 text-contrast-400 cursor-not-allowed opacity-50'
              }`}
            >
              {isUploading ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                  Uploading...
                </>
              ) : (
                'Upload Photo'
              )}
            </button>

            <button
              onClick={() => onClose(false)}
              className="w-full bg-primary-100 text-contrast py-3 rounded-md hover:bg-primary-200 transition-colors"
            >
              Skip for Now
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default AddPhotoSheet;
